import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import {
  Trophy, Dumbbell, Target, Activity, Users, Baby, Heart, Star,
  ArrowRight, ChevronDown, CheckCircle2, Shield, Zap, Clock, Play
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';

// Video Embed Component (DSGVO-konform: Click-to-Load)
const VideoEmbed = ({ videoId }) => {
  const [isPlaying, setIsPlaying] = useState(false);

  if (isPlaying) {
    return (
      <div className="relative rounded-2xl overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.15)] aspect-video">
        <iframe
          src={`https://www.youtube.com/embed/${videoId}?autoplay=1`}
          title="HSC Alswede Vereinsvideo"
          className="absolute top-0 left-0 w-full h-full"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }

  return (
    <button
      onClick={() => setIsPlaying(true)}
      className="relative rounded-2xl overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.15)] aspect-video group cursor-pointer w-full"
    >
      <img
        src={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`}
        alt="Vorschaubild klicken zum Abspielen"
        className="w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors" />
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-20 h-20 bg-[#f5c800] rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
          <Play className="w-8 h-8 text-[#1a1a1a] ml-1" fill="#1a1a1a" />
        </div>
      </div>
      <div className="absolute bottom-4 left-4 right-4">
        <span className="text-white text-sm font-medium">Video abspielen</span>
      </div>
    </button>
  );
};

const FadeIn = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.55, delay, ease: [0.16, 1, 0.3, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Helper function to get badge color based on category
const getCategoryBadgeClass = (category) => {
  if (category === 'Jugend') return 'bg-blue-100 text-blue-700';
  if (category === 'Fest') return 'bg-green-100 text-green-700';
  return 'bg-[#f5c800]/20 text-[#1a1a1a]';
};

const sports = [
  {
    id: 'fussball',
    icon: Trophy,
    title: 'Fußball',
    desc: 'Von Herren über Damen bis zur Altliga – Fußball in allen Altersklassen.',
    badge: 'Beliebtester Sport',
    color: '#f5c800',
  },
  {
    id: 'fitness',
    icon: Dumbbell,
    title: 'Fitness & Aerobic',
    desc: 'Fit am Vormittag, Aero Fighting, Body-Tone – Bewegung für jeden Geschmack.',
    badge: null,
    color: '#f5c800',
  },
  {
    id: 'tischtennis',
    icon: Target,
    title: 'Tischtennis',
    desc: 'Schnell, präzise, spannend – unser Tischtennis-Team freut sich auf dich.',
    badge: 'Wachsend',
    color: '#f5c800',
  },
  {
    id: 'volleyball',
    icon: Activity,
    title: 'Volleyball',
    desc: 'Team-Sport pur: Zusammen trainieren, zusammen gewinnen.',
    badge: null,
    color: '#f5c800',
  },
  {
    id: 'badminton',
    icon: Zap,
    title: 'Badminton',
    desc: 'Schnelle Reflexe, viel Spaß – Badminton für alle Leistungsstufen.',
    badge: null,
    color: '#f5c800',
  },
  {
    id: 'schiessen',
    icon: Target,
    title: 'Sportschießen',
    desc: 'Konzentration, Ruhe, Präzision – Schieße auf 10m Luftgewehr/-pistolenbahnen.',
    badge: null,
    color: '#f5c800',
  },
  {
    id: 'darts',
    icon: Star,
    title: 'Darts',
    desc: 'Neu und beliebt: Darts im HSC. Triff die Bullseye und werde Teil des Teams!',
    badge: 'Neu',
    color: '#f5c800',
  },
  {
    id: 'kinder',
    icon: Baby,
    title: 'Kinder & Jugend',
    desc: 'Mini-Kicker, Kinderturnen, Krabbelgruppe – Sport für die Kleinen ab 0 Jahren.',
    badge: 'Kostenlos u. 6',
    color: '#f5c800',
  },
];

const stats = [
  { id: 'members', value: '450+', label: 'Mitglieder', icon: Users },
  { id: 'history', value: 'Seit 1946', label: 'Vereinsgeschichte', icon: Shield },
  { id: 'sports', value: '10+', label: 'Sportarten', icon: Trophy },
  { id: 'kids', value: 'Gratis', label: 'Kinder unter 6', icon: Heart },
];

const benefits = [
  { id: 'atmosphere', text: 'Familiäre Atmosphäre statt anonymer Geräte' },
  { id: 'variety', text: 'Viele Sportarten – du entscheidest, was dir Spaß macht' },
  { id: 'kids-free', text: 'Kinder unter 6 Jahren trainieren völlig kostenlos' },
  { id: 'volunteer', text: 'Echtes Ehrenamt & echter Zusammenhalt' },
  { id: 'community', text: 'Gemeinschaft bei Events, Festen und Turnieren' },
  { id: 'fees', text: 'Günstige Beiträge für Familien und Mitglieder' },
];

const events = [
  {
    id: 'jhv-2026',
    date: '13.02.2026',
    title: 'Jahreshauptversammlung 2026',
    desc: 'Rückblick auf ein tolles Vereinsjahr – mit Ehrungen für 50-jährige Mitgliedschaft und spannenden Zukunftsplänen.',
    category: 'Verein',
  },
  {
    id: 'minikicker-start',
    date: '15.08.2025',
    title: 'Mini-Kicker Start',
    desc: 'Die HSC Minis sind zurück! Fußball für Kinder von 2–6 Jahren, freitags ab 16:30 Uhr auf dem Sportplatz.',
    category: 'Jugend',
  },
  {
    id: 'fruehstueck-2025',
    date: '16.03.2025',
    title: 'Vereinsfrühstück 2025',
    desc: 'Gemeinsam frühstücken, lachen und den Vereinsgeist stärken – eine geliebte Tradition im HSC.',
    category: 'Fest',
  },
];

const testimonials = [
  {
    id: 'fam-mueller',
    name: 'Familie Müller',
    sport: 'Mini-Kicker',
    quote: 'Unsere Tochter liebt den Mini-Kicker! Sie hat so schnell neue Freunde gefunden – und das völlig kostenlos bis 6 Jahre.',
  },
  {
    id: 'thomas-k',
    name: 'Thomas K.',
    sport: 'Fußball Herren',
    quote: 'Seit 10 Jahren bin ich beim HSC. Die Gemeinschaft hier ist einzigartig – nicht nur Sport, sondern Familie.',
  },
  {
    id: 'margit-s',
    name: 'Margit S.',
    sport: 'Fit am Vormittag',
    quote: 'Als Rentnerin dachte ich, Sport im Verein ist nichts für mich. Heute kann ich den Dienstag ohne meine Gruppe gar nicht mehr denken!',
  },
  {
    id: 'jonas-b',
    name: 'Jonas B.',
    sport: 'Tischtennis',
    quote: 'Darts und Tischtennis – zwei Sportarten, die ich beim HSC für mich entdeckt habe. Toller Verein, tolle Leute!',
  },
];

const sponsors = [
  { id: 'reinkoester', name: 'Reinköster' },
  { id: 'poetter', name: 'Pötter Klima' },
  { id: 'harland', name: 'Harland Bau' },
  { id: 'harting', name: 'Harting' },
  { id: 'stadtwerke', name: 'Stadtwerke Lübbecke' },
  { id: 'lvm', name: 'LVM Jark' },
  { id: 'provinzial', name: 'Provinzial Pohlmeier' },
  { id: 'barre', name: 'Barre' },
  { id: 'kuechen', name: 'Küchen-Center' },
  { id: 'hammer', name: 'Hammer' },
  { id: 'mittwald', name: 'Mittwald' },
  { id: 'marktkauf', name: 'Marktkauf' },
  { id: 'volksbank', name: 'Volksbank' },
  { id: 'gauselmann', name: 'Gauselmann' },
  { id: 'allianz', name: 'Allianz' },
];

export default function Home() {
  useEffect(() => {
    document.title = 'HSC Alswede von 1946 | Sportverein Lübbecke-Alswede';
  }, []);

  return (
    <div className="pt-16">
      {/* ===== HERO ===== */}
      <section
        className="relative min-h-[92vh] flex items-center overflow-hidden"
        style={{
          // backgroundImage: 'url(https://images.pexels.com/photos/30324136/pexels-photo-30324136.jpeg?auto=compress&cs=tinysrgb&w=1400)',
          backgroundImage: 'url(/images/startseite/hero.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
        }}
      >
        <div className="hero-overlay absolute inset-0" />
        <div className="relative z-10 mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-2xl"
          >
            <div className="inline-flex items-center gap-2 bg-[#f5c800] text-[#1a1a1a] text-xs font-semibold px-3 py-1.5 rounded-full mb-6">
              <span>⚽</span>
              <span>Sportverein Lübbecke-Alswede · Seit 1946</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-5" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Dein Verein.<br />
              Dein Team.<br />
              <span className="text-[#f5c800]">Dein Zuhause.</span>
            </h1>
            <p className="text-base sm:text-lg text-white/85 leading-relaxed mb-8 max-w-xl">
              Über 450 Mitglieder, 10+ Sportarten und echte Gemeinschaft.
              Beim HSC Alswede findest du mehr als Sport – du findest Familie.
              Kinder unter 6 Jahren trainieren kostenlos.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/kontakt">
                <Button
                  data-testid="hero-join-button"
                  className="h-12 px-6 bg-[#f5c800] text-[#1a1a1a] hover:bg-[#E6B400] font-semibold rounded-xl border-0 shadow-[0_4px_20px_rgba(245,194,0,0.4)] transition-colors text-base"
                >
                  Jetzt Mitglied werden
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/kontakt">
                <Button
                  data-testid="hero-tryout-button"
                  variant="outline"
                  className="h-12 px-6 bg-white/10 text-white border-white/30 hover:bg-white/20 hover:border-white/50 font-semibold rounded-xl backdrop-blur-sm transition-colors text-base"
                >
                  Probetraining anfragen
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/60 bounce">
          <ChevronDown className="w-6 h-6" />
        </div>
      </section>

      {/* ===== STATS ===== */}
      <section className="bg-[#1a1a1a] py-10">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat) => (
              <FadeIn key={stat.id} delay={stats.indexOf(stat) * 0.1}>
                <div className="text-center" data-testid="stat-item">
                  <div className="text-3xl sm:text-4xl font-bold text-[#f5c800] mb-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {stat.value}
                  </div>
                  <div className="text-sm text-gray-400">{stat.label}</div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ===== INTRO ===== */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <FadeIn>
              <div>
                <div className="section-divider mb-4">
                  <span className="text-xs font-semibold uppercase tracking-widest text-gray-500">Über uns</span>
                </div>
                <h2 className="text-3xl sm:text-4xl font-bold text-[#1a1a1a] mb-6 leading-snug" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Mehr als Sport –{' '}
                  <span className="yellow-underline">echte Gemeinschaft</span>
                </h2>
                <p className="text-gray-600 leading-relaxed mb-5">
                  Der HSC Alswede von 1946 e.V. ist dein Sportverein in Lübbecke-Alswede.
                  Was 1946 als kleiner Fußballclub begann, ist heute ein lebendiger Verein
                  mit über 450 Mitgliedern und einem breiten Sportangebot für jedes Alter.
                </p>
                <p className="text-gray-600 leading-relaxed mb-6">
                  Ob du Fußball spielen, Fitness machen, Tischtennis oder Darts spielen möchtest –
                  oder dein Kind in die Welt des Sports einführen willst: Bei uns bist du richtig.
                </p>
                <Link to="/ueber-uns">
                  <Button variant="outline" className="rounded-xl border-black/20 hover:border-black/40 hover:bg-transparent font-medium">
                    Mehr über uns
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </FadeIn>
            <FadeIn delay={0.15}>
              <div className="relative">
                <div className="rounded-2xl overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.12)]">
                  <img
                    src="https://images.pexels.com/photos/36862523/pexels-photo-36862523.jpeg?auto=compress&cs=tinysrgb&w=800"
                    alt="Fußball beim HSC Alswede"
                    className="w-full h-72 lg:h-80 object-cover"
                  />
                </div>
                <div className="absolute -bottom-4 -left-4 bg-[#f5c800] rounded-2xl p-4 shadow-lg">
                  <div className="text-2xl font-bold text-[#1a1a1a]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>1946</div>
                  <div className="text-xs font-medium text-[#1a1a1a]/70">Gründungsjahr</div>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ===== BENEFITS ===== */}
      <section className="py-16 lg:py-24 bg-[#FAFAF9]" data-testid="benefits-section">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <FadeIn>
              <div>
                <div className="section-divider mb-4">
                  <span className="text-xs font-semibold uppercase tracking-widest text-gray-500">Warum HSC?</span>
                </div>
                <h2 className="text-3xl sm:text-4xl font-bold text-[#1a1a1a] mb-6 leading-snug" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Der Unterschied zum Fitnessstudio
                </h2>
                <p className="text-gray-600 mb-8">
                  Beim HSC Alswede bist du kein Kunde – du bist Mitglied.
                  Das macht den ganzen Unterschied.
                </p>
                <ul className="space-y-3">
                  {benefits.map((benefit) => (
                    <li key={benefit.id} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-[#f5c800] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 text-sm leading-relaxed">{benefit.text}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-8">
                  <Link to="/kontakt">
                    <Button className="h-11 px-6 bg-[#f5c800] text-[#1a1a1a] hover:bg-[#E6B400] font-semibold rounded-xl border-0 shadow-none transition-colors">
                      Kostenlos ausprobieren
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>
            </FadeIn>
            <FadeIn delay={0.15}>
              <div className="grid grid-cols-2 gap-4">
                <Card className="p-5 rounded-2xl border border-black/10 shadow-[0_10px_30px_rgba(17,17,17,0.06)]">
                  <div className="w-10 h-10 bg-[#f5c800]/20 rounded-xl flex items-center justify-center mb-3">
                    <Users className="w-5 h-5 text-[#1a1a1a]" />
                  </div>
                  <div className="font-semibold text-[#1a1a1a] text-sm mb-1">Echte Community</div>
                  <div className="text-xs text-gray-500">450+ Menschen, die füreinander da sind</div>
                </Card>
                <Card className="p-5 rounded-2xl border border-black/10 shadow-[0_10px_30px_rgba(17,17,17,0.06)] mt-6">
                  <div className="w-10 h-10 bg-[#f5c800]/20 rounded-xl flex items-center justify-center mb-3">
                    <Heart className="w-5 h-5 text-[#1a1a1a]" />
                  </div>
                  <div className="font-semibold text-[#1a1a1a] text-sm mb-1">Familie willkommen</div>
                  <div className="text-xs text-gray-500">Kinder unter 6 kostenlos dabei</div>
                </Card>
                <Card className="p-5 rounded-2xl border border-black/10 shadow-[0_10px_30px_rgba(17,17,17,0.06)] -mt-3">
                  <div className="w-10 h-10 bg-[#f5c800]/20 rounded-xl flex items-center justify-center mb-3">
                    <Trophy className="w-5 h-5 text-[#1a1a1a]" />
                  </div>
                  <div className="font-semibold text-[#1a1a1a] text-sm mb-1">10+ Sportarten</div>
                  <div className="text-xs text-gray-500">Für jeden Geschmack das Richtige</div>
                </Card>
                <Card className="p-5 rounded-2xl border border-black/10 shadow-[0_10px_30px_rgba(17,17,17,0.06)]">
                  <div className="w-10 h-10 bg-[#f5c800]/20 rounded-xl flex items-center justify-center mb-3">
                    <Star className="w-5 h-5 text-[#1a1a1a]" />
                  </div>
                  <div className="font-semibold text-[#1a1a1a] text-sm mb-1">Seit 1946</div>
                  <div className="text-xs text-gray-500">Bewährt & verwurzelt in Alswede</div>
                </Card>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ===== SPORTS GRID ===== */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-gray-500 mb-4">
                <span className="w-8 h-0.5 bg-[#f5c800] rounded-full"></span>
                Sportangebote
                <span className="w-8 h-0.5 bg-[#f5c800] rounded-full"></span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1a1a1a]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Sport für jeden – von 0 bis 100
              </h2>
              <p className="mt-3 text-gray-600 max-w-xl mx-auto">
                Ob Fußballstar, Fitness-Enthusiast oder Familienmensch – beim HSC Alswede findest du deinen Sport.
              </p>
            </div>
          </FadeIn>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {sports.map((sport) => (
              <FadeIn key={sport.id} delay={sports.indexOf(sport) * 0.07}>
                <Link to="/sportangebote">
                  <div
                    data-testid="sport-card"
                    className="sport-card relative rounded-2xl border border-black/[0.08] bg-white p-5 shadow-[0_10px_30px_rgba(17,17,17,0.05)] cursor-pointer h-full"
                  >
                    <div className="absolute left-0 top-0 h-full w-1 bg-[#f5c800] rounded-l-2xl" />
                    <div className="pl-2">
                      <div className="flex items-start justify-between mb-3">
                        <div className="w-10 h-10 bg-[#f5c800]/15 rounded-xl flex items-center justify-center">
                          <sport.icon className="w-5 h-5 text-[#1a1a1a]" />
                        </div>
                        {sport.badge && (
                          <Badge className="text-xs bg-[#f5c800]/20 text-[#1a1a1a] border-0 rounded-full">
                            {sport.badge}
                          </Badge>
                        )}
                      </div>
                      <h3 className="font-semibold text-[#1a1a1a] mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                        {sport.title}
                      </h3>
                      <p className="text-xs text-gray-500 leading-relaxed">{sport.desc}</p>
                      <div className="mt-3 flex items-center gap-1 text-xs font-medium text-[#1a1a1a]" data-testid="sport-card-more-link">
                        Mehr erfahren
                        <ArrowRight className="w-3 h-3" />
                      </div>
                    </div>
                  </div>
                </Link>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ===== YOUTH SECTION ===== */}
      <section className="py-16 lg:py-20 bg-[#1a1a1a] relative overflow-hidden">
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: 'radial-gradient(circle at 20% 50%, #f5c800 0%, transparent 50%), radial-gradient(circle at 80% 50%, #f5c800 0%, transparent 50%)'
        }} />
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <FadeIn>
              <div>
                <Badge className="bg-[#f5c800] text-[#1a1a1a] border-0 rounded-full px-3 py-1 text-xs font-semibold mb-5">
                  Jugend & Kinder
                </Badge>
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-5" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Sport beginnt im Kindesalter –
                  <span className="text-[#f5c800]"> bei uns schon ab 0!</span>
                </h2>
                <p className="text-gray-300 leading-relaxed mb-6">
                  Wir investieren in die Zukunft und wollen Kindern früh die Freude am Sport vermitteln.
                  Von der Krabbelgruppe über Kinderturnen bis zu den Mini-Kickern –
                  bei uns ist für jeden das Richtige dabei.
                </p>
                <div className="grid grid-cols-2 gap-3 mb-8">
                  {[
                    { id: 'mini-kicker', label: 'Mini-Kicker', sub: 'Fußball ab 2 Jahren' },
                    { id: 'kinderturnen', label: 'Kinderturnen', sub: 'Motorik & Spaß' },
                    { id: 'krabbelgruppe', label: 'Krabbelgruppe', sub: 'Für die Kleinsten' },
                    { id: 'eltern-kind', label: 'Eltern-Kind', sub: 'Gemeinsam aktiv' },
                  ].map((item) => (
                    <div key={item.id} className="bg-white/[0.07] rounded-xl p-3">
                      <div className="font-semibold text-white text-sm">{item.label}</div>
                      <div className="text-xs text-gray-400 mt-0.5">{item.sub}</div>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-3 bg-[#f5c800]/10 border border-[#f5c800]/20 rounded-xl px-4 py-3">
                  <span className="text-2xl">🎁</span>
                  <div>
                    <div className="font-semibold text-[#f5c800] text-sm">Kinder unter 6 trainieren kostenlos!</div>
                    <div className="text-xs text-gray-400">Einfach vorbeikommen und ausprobieren.</div>
                  </div>
                </div>
              </div>
            </FadeIn>
            <FadeIn delay={0.15}>
              <div className="rounded-2xl overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.3)]">
                <img
                  src="https://images.pexels.com/photos/7427565/pexels-photo-7427565.jpeg?auto=compress&cs=tinysrgb&w=700"
                  alt="Kinderturnen beim HSC Alswede"
                  className="w-full h-72 object-cover"
                />
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ===== EVENTS PREVIEW ===== */}
      <section className="py-16 lg:py-24 bg-[#FAFAF9]">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-10 gap-4">
              <div>
                <div className="section-divider mb-3">
                  <span className="text-xs font-semibold uppercase tracking-widest text-gray-500">Vereinsleben</span>
                </div>
                <h2 className="text-3xl font-bold text-[#1a1a1a]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Was bei uns los ist
                </h2>
              </div>
              <Link to="/vereinsleben">
                <Button variant="outline" className="rounded-xl border-black/20 hover:border-black/40 hover:bg-transparent font-medium text-sm">
                  Alle Events
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </FadeIn>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {events.map((event) => (
              <FadeIn key={event.id} delay={events.indexOf(event) * 0.1}>
                <Card
                  data-testid="event-card"
                  className="event-card rounded-2xl border border-black/[0.08] bg-white p-5 shadow-[0_10px_30px_rgba(17,17,17,0.05)] h-full"
                >
                  <div className="flex items-center justify-between mb-3">
                    <Badge
                      className={`text-xs border-0 rounded-full ${getCategoryBadgeClass(event.category)}`}
                    >
                      {event.category}
                    </Badge>
                    <div className="flex items-center gap-1 text-xs text-gray-400">
                      <Clock className="w-3 h-3" />
                      {event.date}
                    </div>
                  </div>
                  <h3 className="font-semibold text-[#1a1a1a] mb-2 leading-snug" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {event.title}
                  </h3>
                  <p className="text-xs text-gray-500 leading-relaxed">{event.desc}</p>
                </Card>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ===== TESTIMONIALS ===== */}
      <section className="py-16 lg:py-24 bg-white overflow-hidden">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-gray-500 mb-4">
                <span className="w-8 h-0.5 bg-[#f5c800] rounded-full"></span>
                Was unsere Mitglieder sagen
                <span className="w-8 h-0.5 bg-[#f5c800] rounded-full"></span>
              </div>
              <h2 className="text-3xl font-bold text-[#1a1a1a]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Stimmen aus der Vereinsfamilie
              </h2>
            </div>
          </FadeIn>
          <Carousel
            data-testid="testimonials-carousel"
            opts={{ loop: true, align: 'start' }}
            className="w-full"
          >
            <CarouselContent className="-ml-4">
              {testimonials.map((t) => (
                <CarouselItem key={t.id} className="pl-4 md:basis-1/2 lg:basis-1/3">
                  <Card className="rounded-2xl border border-black/[0.08] bg-white p-6 shadow-[0_10px_30px_rgba(17,17,17,0.05)] h-full">
                    <div className="text-2xl mb-3">&ldquo;</div>
                    <p className="text-sm text-gray-600 leading-relaxed mb-4 italic">{t.quote}</p>
                    <div className="flex items-center gap-3 pt-3 border-t border-gray-100">
                      <div className="w-8 h-8 rounded-full bg-[#f5c800]/20 flex items-center justify-center">
                        <Users className="w-4 h-4 text-[#1a1a1a]" />
                      </div>
                      <div>
                        <div className="font-semibold text-[#1a1a1a] text-sm">{t.name}</div>
                        <div className="text-xs text-gray-400">{t.sport}</div>
                      </div>
                    </div>
                  </Card>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="hidden md:flex -left-4" />
            <CarouselNext className="hidden md:flex -right-4" />
          </Carousel>
        </div>
      </section>

      {/* ===== VIDEO ===== */}
      <section className="py-16 lg:py-20 bg-[#FAFAF9]">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-gray-500 mb-4">
                <span className="w-8 h-0.5 bg-[#f5c800] rounded-full"></span>
                Unser Verein in Bewegung
                <span className="w-8 h-0.5 bg-[#f5c800] rounded-full"></span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1a1a1a]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                HSC Alswede – Gemeinsam stark
              </h2>
            </div>
          </FadeIn>
          <FadeIn delay={0.1}>
            <VideoEmbed videoId="F5DbP2TxJhs" />
          </FadeIn>
        </div>
      </section>

      {/* ===== CTA BAND ===== */}
      <section className="py-16 lg:py-20 bg-[#f5c800]">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="text-center">
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1a1a1a] mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Bereit, Teil unserer Gemeinschaft zu werden?
              </h2>
              <p className="text-[#1a1a1a]/70 mb-8 max-w-lg mx-auto">
                Komm einfach zu einem Probetraining vorbei – kostenlos und unverbindlich.
                Wir freuen uns auf dich!
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/kontakt">
                  <Button className="h-12 px-8 bg-[#1a1a1a] text-white hover:bg-[#333] font-semibold rounded-xl border-0 shadow-none transition-colors text-base">
                    Jetzt Mitglied werden
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/sportangebote">
                  <Button variant="outline" className="h-12 px-8 bg-transparent text-[#1a1a1a] border-[#1a1a1a]/30 hover:border-[#1a1a1a] hover:bg-transparent font-semibold rounded-xl transition-colors text-base">
                    Sportangebote entdecken
                  </Button>
                </Link>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ===== SPONSORS ===== */}
      <section className="py-12 bg-white border-t border-gray-100">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <p className="text-center text-xs font-semibold uppercase tracking-widest text-gray-400 mb-8">
              Unsere Sponsoren & Partner
            </p>
          </FadeIn>
          <div className="flex flex-wrap justify-center gap-6 items-center" data-testid="sponsors-carousel">
            {sponsors.map((sponsor) => (
              <FadeIn key={sponsor.id} delay={sponsors.indexOf(sponsor) * 0.03}>
                <div className="px-5 py-2.5 rounded-xl border border-gray-200 bg-gray-50 hover:bg-[#f5c800]/10 hover:border-[#f5c800]/40 transition-colors cursor-default">
                  <span className="text-sm font-medium text-gray-500 whitespace-nowrap">{sponsor.name}</span>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
